import { joinBackward, joinForward } from 'prosemirror-commands';
import { TextSelection } from 'prosemirror-state';

const ADD_MARK_NAME = 'diffSuggestionAdd';
const REMOVE_MARK_NAME = 'diffSuggestionRemove';
export const EMPTY_LINE_SUGGESTION_CHAR = '\u00A0';
export const PARAGRAPH_BREAK_SUGGESTION_CHAR = '\u2060';
export const HARD_BREAK_SUGGESTION_CHAR = '\u2063';

export const isStructuralSuggestionMarkerChar = (value) => {
  const char = String(value || '');
  return (
    char === EMPTY_LINE_SUGGESTION_CHAR
    || char === PARAGRAPH_BREAK_SUGGESTION_CHAR
    || char === HARD_BREAK_SUGGESTION_CHAR
  );
};

const normalizeAnnotationId = (value) => String(value || '').trim();
const normalizeBatchId = (value) => String(value || '').trim();

const randomBase62 = (size = 8) => {
  const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  let out = '';
  for (let index = 0; index < size; index += 1) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
};

const DEFAULT_SUGGESTION_ID_SIZE = 6;

const normalizeStatus = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'accepted' || normalized === 'rejected') return normalized;
  return 'pending';
};

const normalizeSource = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'user_suggest' || normalized === 'compare') return normalized;
  return 'ai_write';
};

const clampDocPos = (doc, pos) => {
  const max = Number(doc?.content?.size || 0);
  return Math.max(0, Math.min(Number(pos || 0), max));
};

export const normalizeExistingSuggestionAttrs = (attrs = {}, op = 'add') => {
  const annotationId = String(attrs?.annotation_id || '').trim();
  const batchId = String(attrs?.batch_id || '').trim();
  if (!annotationId || !batchId) return null;
  return {
    annotation_id: annotationId,
    batch_id: batchId,
    diff_op: op === 'remove' ? 'remove' : 'add',
    source: String(attrs?.source || '').trim() || 'user_suggest',
    author_id: attrs?.author_id == null ? null : String(attrs.author_id),
    created_at: Number.isFinite(Number(attrs?.created_at)) && Number(attrs?.created_at) > 0
      ? Math.floor(Number(attrs.created_at))
      : Date.now(),
    status: String(attrs?.status || '').trim().toLowerCase() || 'pending',
    diff_variant_of: String(attrs?.diff_variant_of || '').trim() || null
  };
};

export const findAdjacentSuggestionAttrs = (state, from, to, markName) => {
  const doc = state?.doc;
  const markType = state?.schema?.marks?.[markName];
  if (!doc || !markType) return null;

  const candidatePositions = [
    clampDocPos(doc, from - 1),
    clampDocPos(doc, from),
    clampDocPos(doc, to - 1),
    clampDocPos(doc, to)
  ];

  for (const pos of candidatePositions) {
    let $pos;
    try {
      $pos = doc.resolve(pos);
    } catch (err) {
      $pos = null;
    }
    if (!$pos) continue;

    const markCandidates = [
      ...($pos.marks() || []),
      ...($pos.nodeBefore?.marks || []),
      ...($pos.nodeAfter?.marks || [])
    ];

    const match = markCandidates.find((mark) => {
      if (!mark || mark.type !== markType) return false;
      const source = String(mark?.attrs?.source || '').trim().toLowerCase();
      const status = String(mark?.attrs?.status || '').trim().toLowerCase();
      // Never reuse a structural boundary marker's attrs — the marker has its own
      // annotation_id and must stay isolated. Reusing it would cause adjacent
      // regular-text deletes to share the same annotation_id as the marker,
      // then be incorrectly deleted when the marker is kept.
      const markerType = String(mark?.attrs?.marker_type || '').trim().toLowerCase();
      if (markerType === 'paragraph-break' || markerType === 'hard-break') return false;
      return source === 'user_suggest' && status === 'pending';
    });

    if (match?.attrs) {
      return match.attrs;
    }
  }

  return null;
};

export const rangeContainsOnlyAddSuggestionText = (state, from, to) => {
  const doc = state?.doc;
  const addMarkType = state?.schema?.marks?.[ADD_MARK_NAME];
  const removeMarkType = state?.schema?.marks?.[REMOVE_MARK_NAME];
  if (!doc || !addMarkType || !Number.isFinite(from) || !Number.isFinite(to) || to <= from) {
    return false;
  }

  let sawText = false;
  let hasNonAddText = false;

  doc.nodesBetween(from, to, (node, pos) => {
    if (!node?.isText) return;

    const nodeStart = Number(pos || 0);
    const nodeEnd = nodeStart + Number(node.nodeSize || 0);
    const overlapFrom = Math.max(from, nodeStart);
    const overlapTo = Math.min(to, nodeEnd);
    if (overlapTo <= overlapFrom) return;

    sawText = true;
    const marks = Array.isArray(node.marks) ? node.marks : [];
    const hasAdd = marks.some((mark) => mark?.type === addMarkType);
    const hasRemove = Boolean(removeMarkType) && marks.some((mark) => mark?.type === removeMarkType);

    if (!hasAdd || hasRemove) {
      hasNonAddText = true;
    }
  });

  return sawText && !hasNonAddText;
};

export const caretHasMarkType = (state, pos, markType) => {
  if (!state?.doc || !markType || !Number.isFinite(pos)) return false;
  const doc = state.doc;
  const exactPos = clampDocPos(doc, pos);
  let $pos;
  try {
    $pos = doc.resolve(exactPos);
  } catch (err) {
    $pos = null;
  }
  if (!$pos) return false;

  const marks = $pos.marks() || [];
  return marks.some((mark) => mark?.type === markType);
};

export const rangeIntersectsSuggestionMark = (state, from, to, markName) => {
  const doc = state?.doc;
  const markType = state?.schema?.marks?.[markName];
  if (!doc || !markType || !Number.isFinite(from) || !Number.isFinite(to)) return false;

  if (to <= from) {
    return caretHasMarkType(state, from, markType);
  }

  let intersects = false;
  doc.nodesBetween(from, to, (node) => {
    if (!node?.isText) return;
    const marks = Array.isArray(node.marks) ? node.marks : [];
    if (marks.some((mark) => mark?.type === markType)) {
      intersects = true;
    }
  });

  return intersects;
};

export const getTextOverlapRanges = ({ state, from, to } = {}) => {
  const doc = state?.doc;
  if (!doc || !Number.isFinite(from) || !Number.isFinite(to) || to <= from) return [];

  const ranges = [];
  doc.nodesBetween(from, to, (node, pos) => {
    if (!node?.isText) return;
    const nodeStart = Number(pos || 0);
    const nodeEnd = nodeStart + Number(node.nodeSize || 0);
    const overlapFrom = Math.max(from, nodeStart);
    const overlapTo = Math.min(to, nodeEnd);
    if (overlapTo > overlapFrom) {
      ranges.push({ from: overlapFrom, to: overlapTo });
    }
  });

  return ranges;
};

export const shouldUseNativePasteBypass = ({ bypassStructuredFormatting = false, isSuggestMode = false } = {}) => {
  return Boolean(bypassStructuredFormatting) && !Boolean(isSuggestMode);
};

export const getFallbackPasteText = ({ rawText = '', html = '' } = {}) => {
  const text = String(rawText || '');
  if (text) return text;

  const markup = String(html || '');
  if (!markup) return '';

  try {
    const host = document.createElement('div');
    host.innerHTML = markup;
    return String(host.textContent || '');
  } catch (err) {
    return '';
  }
};

export const shouldHandleStructuralEmptyDelete = ({ selection, targetText = '' } = {}) => {
  if (!selection || !selection.empty) return false;
  if (String(targetText || '')) return false;

  const parent = selection?.$from?.parent;
  const isEmptyTextblock = Boolean(parent?.isTextblock) && Number(parent?.content?.size || 0) === 0;
  return isEmptyTextblock;
};

export const shouldHandleStructuralHardBreakDelete = ({ state, selection, isBackwardDelete = true } = {}) => {
  if (!selection || !selection.empty) return false;
  const $from = selection?.$from;
  const adjacentNode = isBackwardDelete ? $from?.nodeBefore : $from?.nodeAfter;
  const adjacentNodeType = String(adjacentNode?.type?.name || '').trim().toLowerCase();
  if (adjacentNodeType === 'hardbreak') return true;

  if (!isBackwardDelete) {
    const atBlockEnd = Number($from?.parentOffset || 0) === Number($from?.parent?.content?.size || 0);
    const backwardNodeType = String($from?.nodeBefore?.type?.name || '').trim().toLowerCase();
    if (atBlockEnd && backwardNodeType === 'hardbreak') {
      return true;
    }
  }

  if (isBackwardDelete) {
    const atBlockStart = Number($from?.parentOffset || 0) === 0;
    const forwardNodeType = String($from?.nodeAfter?.type?.name || '').trim().toLowerCase();
    if (atBlockStart && forwardNodeType === 'hardbreak') {
      return true;
    }
  }

  // Backspace at the start of a textblock can sit on a block boundary where
  // the previous character is a hardBreak in the prior block.
  if (!isBackwardDelete) return false;

  const atBlockStart = Number($from?.parentOffset || 0) === 0;
  if (!atBlockStart) return false;

  const doc = state?.doc;
  const cursorPos = Number(selection?.from ?? $from?.pos);
  if (!doc || !Number.isFinite(cursorPos) || cursorPos <= 0) return false;

  let $prev;
  try {
    $prev = doc.resolve(clampDocPos(doc, cursorPos - 1));
  } catch (err) {
    return false;
  }

  const prevAfterType = String($prev?.nodeAfter?.type?.name || '').trim().toLowerCase();
  return prevAfterType === 'hardbreak';
};

export const getStructuralHardBreakMarkerPos = ({ state, selection, isBackwardDelete = true } = {}) => {
  if (!shouldHandleStructuralHardBreakDelete({ state, selection, isBackwardDelete })) return null;

  const cursorPos = Number(selection?.from ?? selection?.$from?.pos);
  if (!Number.isFinite(cursorPos)) return null;

  if (isBackwardDelete) {
    const atBlockStart = Number(selection?.$from?.parentOffset || 0) === 0;
    const forwardNodeType = String(selection?.$from?.nodeAfter?.type?.name || '').trim().toLowerCase();
    if (atBlockStart && forwardNodeType === 'hardbreak') {
      return Math.max(0, cursorPos);
    }

    const doc = state?.doc;
    if (atBlockStart && doc && cursorPos > 0) {
      let $prev;
      try {
        $prev = doc.resolve(clampDocPos(doc, cursorPos - 1));
      } catch (err) {
        $prev = null;
      }
      const prevAfterType = String($prev?.nodeAfter?.type?.name || '').trim().toLowerCase();
      if (prevAfterType === 'hardbreak') {
        return Math.max(0, cursorPos - 1);
      }
    }
  }

  if (!isBackwardDelete) {
    const atBlockEnd = Number(selection?.$from?.parentOffset || 0) === Number(selection?.$from?.parent?.content?.size || 0);
    const backwardNodeType = String(selection?.$from?.nodeBefore?.type?.name || '').trim().toLowerCase();
    if (atBlockEnd && backwardNodeType === 'hardbreak') {
      return Math.max(0, cursorPos - 1);
    }
  }

  return isBackwardDelete
    ? Math.max(0, cursorPos - 1)
    : Math.max(0, cursorPos);
};

export const isSelectionInListItemContext = ({ selection } = {}) => {
  const $from = selection?.$from;
  if (!$from || typeof $from.depth !== 'number' || typeof $from.node !== 'function') return false;

  for (let depth = $from.depth; depth >= 0; depth -= 1) {
    const name = String($from.node(depth)?.type?.name || '').trim().toLowerCase();
    if (name === 'listitem' || name === 'taskitem') return true;
  }

  return false;
};

export const shouldHandleStructuralSuggestEnter = ({ selection } = {}) => {
  return !isSelectionInListItemContext({ selection });
};

export const shouldInjectListItemSuggestionMarker = ({ selection } = {}) => {
  if (!selection?.empty) return false;
  if (!isSelectionInListItemContext({ selection })) return false;
  const parent = selection?.$from?.parent;
  return Boolean(parent?.isTextblock) && Number(parent?.content?.size || 0) === 0;
};

export const getSingleAddSuggestionPlaceholderRange = ({ state, selection } = {}) => {
  if (!state?.doc || !selection?.empty) return null;
  if (!isSelectionInListItemContext({ selection })) return null;

  const $from = selection?.$from;
  const parent = $from?.parent;
  const parentSize = Number(parent?.content?.size || 0);
  if (!parent?.isTextblock || parentSize !== 1 || typeof $from?.start !== 'function') return null;

  const start = Number($from.start());
  const end = start + 1;
  if (!Number.isFinite(start) || start < 0) return null;

  const text = String(state.doc.textBetween(start, end, '', '') || '');
  if (text !== EMPTY_LINE_SUGGESTION_CHAR) return null;

  if (!rangeContainsOnlyAddSuggestionText(state, start, end)) return null;

  return { from: start, to: end };
};

export const getRemovableSuggestedListItemRange = ({ state, selection } = {}) => {
  if (!state?.doc || !selection?.empty) return null;
  if (!isSelectionInListItemContext({ selection })) return null;

  const $from = selection?.$from;
  if (!$from || typeof $from.depth !== 'number' || typeof $from.node !== 'function') return null;

  let listDepth = -1;
  for (let depth = $from.depth; depth >= 0; depth -= 1) {
    const name = String($from.node(depth)?.type?.name || '').trim().toLowerCase();
    if (name === 'listitem' || name === 'taskitem') {
      listDepth = depth;
      break;
    }
  }
  if (listDepth <= 0) return null;

  const listNode = $from.node(listDepth);
  if (!listNode || Number(listNode?.childCount || 0) !== 1) return null;

  const textblock = listNode.firstChild;
  const textblockSize = Number(textblock?.content?.size || 0);
  if (!textblock?.isTextblock || textblockSize !== 1) return null;

  const textPos = Number($from.start(listDepth + 1));
  if (!Number.isFinite(textPos) || textPos < 0) return null;

  const text = String(state.doc.textBetween(textPos, textPos + 1, '', '') || '');
  if (text !== EMPTY_LINE_SUGGESTION_CHAR) return null;
  if (!rangeContainsOnlyAddSuggestionText(state, textPos, textPos + 1)) return null;

  const from = Number($from.before(listDepth));
  const to = Number($from.after(listDepth));
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;

  return { from, to };
};

export const getRemovableSuggestedListItemRangeForContentDeletion = ({ state, from, to } = {}) => {
  const doc = state?.doc;
  if (!doc || !Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;

  let $from;
  let $to;
  try {
    $from = doc.resolve(clampDocPos(doc, from));
    $to = doc.resolve(clampDocPos(doc, Math.max(from, to - 1)));
  } catch (err) {
    return null;
  }
  if (!$from || !$to) return null;

  const fromDepth = findListItemDepth($from);
  const toDepth = findListItemDepth($to);
  if (fromDepth <= 0 || toDepth <= 0 || fromDepth !== toDepth) return null;

  const fromListStart = Number($from.before(fromDepth));
  const toListStart = Number($to.before(toDepth));
  if (!Number.isFinite(fromListStart) || !Number.isFinite(toListStart) || fromListStart !== toListStart) return null;

  const listNode = $from.node(fromDepth);
  if (!listNode || Number(listNode?.childCount || 0) !== 1) return null;
  const textblock = listNode.firstChild;
  if (!textblock?.isTextblock) return null;

  const contentStart = Number($from.start(fromDepth + 1));
  const contentSize = Number(textblock?.content?.size || 0);
  const contentEnd = contentStart + contentSize;
  if (!Number.isFinite(contentStart) || !Number.isFinite(contentEnd) || contentEnd <= contentStart) return null;

  if (from > contentStart || to < contentEnd) return null;
  if (!rangeContainsOnlyAddSuggestionText(state, contentStart, contentEnd)) return null;

  const listFrom = Number($from.before(fromDepth));
  const listTo = Number($from.after(fromDepth));
  if (!Number.isFinite(listFrom) || !Number.isFinite(listTo) || listTo <= listFrom) return null;

  return { from: listFrom, to: listTo };
};

export const shouldBlockBoundaryDeleteOnRemoveSuggestedListItem = ({ state, selection, isBackwardDelete = true } = {}) => {
  if (!state?.doc || !selection?.empty) return false;
  if (!isSelectionInListItemContext({ selection })) return false;

  const $from = selection?.$from;
  const parent = $from?.parent;
  if (!parent?.isTextblock) return false;

  const atBoundary = isBackwardDelete
    ? Number($from?.parentOffset || 0) === 0
    : Number($from?.parentOffset || 0) === Number(parent?.content?.size || 0);
  if (!atBoundary) return false;

  const contentStart = Number($from?.start?.());
  const contentSize = Number(parent?.content?.size || 0);
  const contentEnd = contentStart + contentSize;
  if (!Number.isFinite(contentStart) || !Number.isFinite(contentEnd) || contentEnd <= contentStart) return false;

  return rangeIntersectsSuggestionMark(state, contentStart, contentEnd, REMOVE_MARK_NAME);
};

export const createSuggestionIdentity = ({
  annotationId = null,
  batchId = null,
  prefix = 'sug'
} = {}) => {
  const normalizedAnnotationId = normalizeAnnotationId(annotationId) || randomBase62(DEFAULT_SUGGESTION_ID_SIZE);
  const normalizedBatchId = normalizeBatchId(batchId) || randomBase62(DEFAULT_SUGGESTION_ID_SIZE);
  return {
    annotationId: normalizedAnnotationId,
    batchId: normalizedBatchId
  };
};

export const createSuggestionMarkAttrs = ({
  op = 'add',
  annotationId = null,
  batchId = null,
  source = 'ai_write',
  authorId = null,
  createdAt = Date.now(),
  status = 'pending',
  variantOf = null,
  markerType = null,
  markerDirection = null
} = {}) => {
  const identity = createSuggestionIdentity({ annotationId, batchId });
  const normalizedMarkerType = String(markerType || '').trim().toLowerCase();
  const normalizedMarkerDirection = String(markerDirection || '').trim().toLowerCase();

  return {
    annotation_id: identity.annotationId,
    batch_id: identity.batchId,
    diff_op: String(op || '').trim().toLowerCase() === 'remove' ? 'remove' : 'add',
    source: normalizeSource(source),
    author_id: authorId == null ? null : String(authorId),
    created_at: Number.isFinite(Number(createdAt)) && Number(createdAt) > 0 ? Math.floor(Number(createdAt)) : Date.now(),
    status: normalizeStatus(status),
    diff_variant_of: normalizeAnnotationId(variantOf) || null,
    ...(normalizedMarkerType ? { marker_type: normalizedMarkerType } : {}),
    ...((normalizedMarkerDirection === 'backspace' || normalizedMarkerDirection === 'delete')
      ? { marker_direction: normalizedMarkerDirection }
      : {})
  };
};

export const buildSuggestionTextNode = ({
  text = '',
  op = 'add',
  attrs = {}
} = {}) => {
  const markType = String(op || '').trim().toLowerCase() === 'remove' ? REMOVE_MARK_NAME : ADD_MARK_NAME;
  return {
    type: 'text',
    text: String(text || ''),
    marks: [{ type: markType, attrs: { ...attrs, diff_op: markType === REMOVE_MARK_NAME ? 'remove' : 'add' } }]
  };
};

export const buildAiWriteSuggestionPair = ({
  previousText = '',
  nextText = '',
  authorId = null,
  batchId = null,
  createdAt = Date.now(),
  variantOf = null
} = {}) => {
  const identity = createSuggestionIdentity({ batchId });
  const removeAttrs = createSuggestionMarkAttrs({
    op: 'remove',
    annotationId: identity.annotationId,
    batchId: identity.batchId,
    source: 'ai_write',
    authorId,
    createdAt,
    status: 'pending',
    variantOf
  });
  const addAttrs = createSuggestionMarkAttrs({
    op: 'add',
    annotationId: identity.annotationId,
    batchId: identity.batchId,
    source: 'ai_write',
    authorId,
    createdAt,
    status: 'pending',
    variantOf
  });

  return {
    annotationId: identity.annotationId,
    batchId: identity.batchId,
    removedNode: buildSuggestionTextNode({ text: previousText, op: 'remove', attrs: removeAttrs }),
    addedNode: buildSuggestionTextNode({ text: nextText, op: 'add', attrs: addAttrs })
  };
};

const collectMarkRanges = (state, matchFn) => {
  const addRanges = [];
  const removeRanges = [];

  if (!state?.doc || typeof matchFn !== 'function') {
    return { addRanges, removeRanges };
  }

  const addType = state.schema?.marks?.[ADD_MARK_NAME] || null;
  const removeType = state.schema?.marks?.[REMOVE_MARK_NAME] || null;

  if (!addType && !removeType) {
    return { addRanges, removeRanges };
  }

  state.doc.descendants((node, pos) => {
    if (!node?.isText || !Array.isArray(node.marks) || node.marks.length === 0) return;

    const from = pos;
    const to = pos + node.nodeSize;

    for (const mark of node.marks) {
      if (!matchFn(mark)) continue;
      if (addType && mark.type === addType) {
        addRanges.push({ from, to });
      }
      if (removeType && mark.type === removeType) {
        removeRanges.push({ from, to });
      }
    }
  });

  return { addRanges, removeRanges };
};

const collectMarkRangesByAnnotationId = (state, annotationId) => {
  const normalizedId = normalizeAnnotationId(annotationId);
  if (!normalizedId) {
    return { addRanges: [], removeRanges: [] };
  }

  return collectMarkRanges(state, (mark) => {
    const markAnnotationId = normalizeAnnotationId(mark?.attrs?.annotation_id);
    return Boolean(markAnnotationId) && markAnnotationId === normalizedId;
  });
};

const collectMarkRangesByBatchId = (state, batchId) => {
  const normalizedBatchId = normalizeBatchId(batchId);
  if (!normalizedBatchId) {
    return { addRanges: [], removeRanges: [] };
  }

  return collectMarkRanges(state, (mark) => {
    const markBatchId = normalizeBatchId(mark?.attrs?.batch_id);
    return Boolean(markBatchId) && markBatchId === normalizedBatchId;
  });
};

const sortRangesDescending = (ranges = []) => {
  return [...ranges].sort((a, b) => {
    if (a.from !== b.from) return b.from - a.from;
    return b.to - a.to;
  });
};

const coalesceDescendingRanges = (ranges = []) => {
  if (!Array.isArray(ranges) || ranges.length === 0) return [];
  const sorted = sortRangesDescending(ranges)
    .filter((range) => Number.isFinite(range?.from) && Number.isFinite(range?.to) && range.to > range.from);
  const result = [];

  sorted.forEach((range) => {
    const last = result[result.length - 1];
    if (!last) {
      result.push({ from: range.from, to: range.to });
      return;
    }

    if (range.to >= last.from) {
      last.from = Math.min(last.from, range.from);
      last.to = Math.max(last.to, range.to);
      return;
    }

    result.push({ from: range.from, to: range.to });
  });

  return result;
};

const getListItemRangeForStructuralAddPlaceholder = (state, range) => {
  const doc = state?.doc;
  const from = Number(range?.from);
  const to = Number(range?.to);
  if (!doc || !Number.isFinite(from) || !Number.isFinite(to) || to - from !== 1) return null;
  if (typeof doc.textBetween !== 'function' || typeof doc.resolve !== 'function') return null;

  const token = String(doc.textBetween(from, to, '', '') || '');
  if (token !== '\u00A0') return null;

  let $pos;
  try {
    $pos = doc.resolve(from);
  } catch (err) {
    return null;
  }
  if (!$pos || typeof $pos.depth !== 'number' || typeof $pos.node !== 'function') return null;

  for (let depth = $pos.depth; depth >= 0; depth -= 1) {
    const node = $pos.node(depth);
    const name = String(node?.type?.name || '').trim().toLowerCase();
    if (name !== 'listitem' && name !== 'taskitem') continue;

    if (Number(node?.childCount || 0) !== 1) return null;
    const textblock = node.firstChild;
    if (!textblock?.isTextblock || Number(textblock?.content?.size || 0) !== 1) return null;

    const textStart = Number($pos.start(depth + 1));
    if (!Number.isFinite(textStart) || textStart !== from) return null;

    const listFrom = Number($pos.before(depth));
    const listTo = Number($pos.after(depth));
    if (!Number.isFinite(listFrom) || !Number.isFinite(listTo) || listTo <= listFrom) return null;

    return { from: listFrom, to: listTo };
  }

  return null;
};

const getUndoDeleteRanges = (state, addRanges = []) => {
  if (!Array.isArray(addRanges) || addRanges.length === 0) return [];
  const getTextblockRangeForFullAddCoverage = (range) => {
    const doc = state?.doc;
    const from = Number(range?.from);
    const to = Number(range?.to);
    if (!doc || !Number.isFinite(from) || !Number.isFinite(to) || to <= from || typeof doc.resolve !== 'function') return null;

    let $from;
    let $to;
    try {
      $from = doc.resolve(from);
      $to = doc.resolve(Math.max(from, to - 1));
    } catch (err) {
      return null;
    }

    if (!$from?.parent?.isTextblock || !$to?.parent?.isTextblock) return null;
    if ($from.depth !== $to.depth) return null;

    const contentStart = Number($from.start($from.depth));
    const contentEnd = contentStart + Number($from.parent?.content?.size || 0);
    if (!Number.isFinite(contentStart) || !Number.isFinite(contentEnd) || contentEnd <= contentStart) return null;
    if (from > contentStart || to < contentEnd) return null;

    const blockFrom = Number($from.before($from.depth));
    const blockTo = Number($from.after($from.depth));
    if (!Number.isFinite(blockFrom) || !Number.isFinite(blockTo) || blockTo <= blockFrom) return null;
    return { from: blockFrom, to: blockTo };
  };

  const promoted = addRanges.map((range) => {
    return (
      getListItemRangeForStructuralAddPlaceholder(state, range)
      || getTextblockRangeForFullAddCoverage(range)
      || range
    );
  });
  return coalesceDescendingRanges(promoted);
};

const findListItemDepth = ($pos) => {
  if (!$pos || typeof $pos.depth !== 'number' || typeof $pos.node !== 'function') return -1;
  for (let depth = $pos.depth; depth >= 0; depth -= 1) {
    const name = String($pos.node(depth)?.type?.name || '').trim().toLowerCase();
    if (name === 'listitem' || name === 'taskitem') return depth;
  }
  return -1;
};

const getListItemRangeForStructuralRemovePlaceholder = (state, range) => {
  const doc = state?.doc;
  const from = Number(range?.from);
  const to = Number(range?.to);
  if (!doc || !Number.isFinite(from) || !Number.isFinite(to) || to - from !== 1) return null;
  if (typeof doc.textBetween !== 'function' || typeof doc.resolve !== 'function') return null;

  const token = String(doc.textBetween(from, to, '', '') || '');
  if (token !== EMPTY_LINE_SUGGESTION_CHAR) return null;

  let $pos;
  try {
    $pos = doc.resolve(from);
  } catch (err) {
    return null;
  }

  const listDepth = findListItemDepth($pos);
  if (listDepth <= 0) return null;

  const listNode = $pos.node(listDepth);
  if (!listNode || Number(listNode?.childCount || 0) !== 1) return null;
  const textblock = listNode.firstChild;
  if (!textblock?.isTextblock || Number(textblock?.content?.size || 0) !== 1) return null;

  const textStart = Number($pos.start(listDepth + 1));
  if (!Number.isFinite(textStart) || textStart !== from) return null;

  const listFrom = Number($pos.before(listDepth));
  const listTo = Number($pos.after(listDepth));
  if (!Number.isFinite(listFrom) || !Number.isFinite(listTo) || listTo <= listFrom) return null;

  return { from: listFrom, to: listTo };
};

const getKeepDeleteRanges = (state, removeRanges = []) => {
  if (!Array.isArray(removeRanges) || removeRanges.length === 0) return [];
  const doc = state?.doc;
  if (!doc || typeof doc.resolve !== 'function') {
    return sortRangesDescending(removeRanges);
  }

  const promotedRanges = [];
  const coveredContentRanges = [];
  const remainingRanges = [];

  removeRanges.forEach((range) => {
    const promoted = getListItemRangeForStructuralRemovePlaceholder(state, range);
    if (promoted) {
      promotedRanges.push(promoted);
      return;
    }
    remainingRanges.push(range);
  });

  const candidatesByKey = new Map();

  remainingRanges.forEach((range) => {
    const from = Number(range?.from);
    const to = Number(range?.to);
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return;

    let $from;
    let $to;
    try {
      $from = doc.resolve(from);
      $to = doc.resolve(Math.max(from, to - 1));
    } catch (err) {
      return;
    }

    const fromDepth = findListItemDepth($from);
    const toDepth = findListItemDepth($to);
    if (fromDepth <= 0 || toDepth <= 0 || fromDepth !== toDepth) return;

    const listFrom = Number($from.before(fromDepth));
    const listTo = Number($from.after(fromDepth));
    const toListFrom = Number($to.before(toDepth));
    if (!Number.isFinite(listFrom) || !Number.isFinite(listTo) || !Number.isFinite(toListFrom) || listTo <= listFrom || listFrom !== toListFrom) return;

    const listNode = $from.node(fromDepth);
    if (!listNode || Number(listNode?.childCount || 0) !== 1) return;
    const textblock = listNode.firstChild;
    if (!textblock?.isTextblock) return;

    const contentStart = Number($from.start(fromDepth + 1));
    const contentEnd = contentStart + Number(textblock?.content?.size || 0);
    if (!Number.isFinite(contentStart) || !Number.isFinite(contentEnd) || contentEnd <= contentStart) return;

    const overlapFrom = Math.max(from, contentStart);
    const overlapTo = Math.min(to, contentEnd);
    if (overlapTo <= overlapFrom) return;

    const key = `${listFrom}:${listTo}`;
    const existing = candidatesByKey.get(key);
    if (existing) {
      existing.ranges.push({ from: overlapFrom, to: overlapTo });
      return;
    }

    candidatesByKey.set(key, {
      listFrom,
      listTo,
      contentStart,
      contentEnd,
      ranges: [{ from: overlapFrom, to: overlapTo }]
    });
  });

  candidatesByKey.forEach((candidate) => {
    const merged = coalesceDescendingRanges(candidate.ranges);
    if (!merged.length) return;
    const first = merged[merged.length - 1];
    const last = merged[0];
    const coversAll = first.from <= candidate.contentStart && last.to >= candidate.contentEnd;
    if (!coversAll) return;

    promotedRanges.push({ from: candidate.listFrom, to: candidate.listTo });
    coveredContentRanges.push({ from: candidate.contentStart, to: candidate.contentEnd });
  });

  const nonPromotedRanges = remainingRanges.filter((range) => {
    const from = Number(range?.from);
    const to = Number(range?.to);
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return false;
    return !coveredContentRanges.some((covered) => from >= covered.from && to <= covered.to);
  });

  return coalesceDescendingRanges([...nonPromotedRanges, ...promotedRanges]);
};

const getStructuralDeleteRanges = (structuralNodes = []) => {
  return sortRangesDescending(
    (Array.isArray(structuralNodes) ? structuralNodes : [])
      .map((node) => {
        const from = Number(node?.pos);
        const nodeSize = Number(node?.nodeSize);
        const to = from + nodeSize;
        if (!Number.isFinite(from) || !Number.isFinite(nodeSize) || !Number.isFinite(to) || to <= from) {
          return null;
        }
        return { from, to };
      })
      .filter(Boolean)
  );
};

const mergeKeepDeleteRanges = (state, removeRanges = [], structuralNodes = []) => {
  const structuralRanges = getStructuralDeleteRanges(structuralNodes);
  const markDeleteRanges = getKeepDeleteRanges(state, removeRanges).filter((range) => {
    const from = Number(range?.from);
    const to = Number(range?.to);
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return false;
    // If a mark delete range is fully enclosed by a structural range, drop it.
    // The structural delete already removes that content and avoids shifted-pos issues.
    return !structuralRanges.some((sr) => from >= sr.from && to <= sr.to);
  });
  return sortRangesDescending([...markDeleteRanges, ...structuralRanges]);
};

const isStructuralRemoveNode = (node) => String(node?.attrs?.diffOp || '').trim().toLowerCase() === 'remove';
const isStructuralAddNode = (node) => String(node?.attrs?.diffOp || '').trim().toLowerCase() === 'add';

const clearStructuralSuggestionAttrs = (attrs = {}) => ({
  ...attrs,
  diffOp: null,
  diffAnnotationId: null,
  diffBatchId: null
});

const mergeUndoDeleteRanges = (addRanges = [], structuralAddNodes = []) => {
  const structuralRanges = getStructuralDeleteRanges(structuralAddNodes);
  const markDeleteRanges = coalesceDescendingRanges(addRanges).filter((range) => {
    const from = Number(range?.from);
    const to = Number(range?.to);
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return false;
    return !structuralRanges.some((sr) => from >= sr.from && to <= sr.to);
  });
  return sortRangesDescending([...markDeleteRanges, ...structuralRanges]);
};

const resolveAncestorTableRange = (doc, pos) => {
  if (!doc || !Number.isFinite(pos) || typeof doc.resolve !== 'function') return null;
  let $pos;
  try {
    $pos = doc.resolve(Math.max(0, Math.min(Number(pos), Number(doc?.content?.size || 0))));
  } catch (err) {
    return null;
  }
  if (!$pos || typeof $pos.depth !== 'number' || typeof $pos.node !== 'function') return null;

  for (let depth = $pos.depth; depth > 0; depth -= 1) {
    const node = $pos.node(depth);
    const name = String(node?.type?.name || '').trim();
    if (name !== 'table') continue;
    const from = Number($pos.before(depth));
    const to = Number($pos.after(depth));
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;
    return { from, to };
  }

  return null;
};

const mapPosInTransaction = (tr, pos, assoc = 1) => {
  const rawPos = Number(pos);
  if (!Number.isFinite(rawPos)) return null;

  let mappedPos = rawPos;
  try {
    if (tr?.mapping && typeof tr.mapping.map === 'function') {
      mappedPos = Number(tr.mapping.map(rawPos, assoc));
    }
  } catch (err) {
    mappedPos = rawPos;
  }

  if (!Number.isFinite(mappedPos)) return null;

  const maxPos = Number(tr?.doc?.content?.size);
  if (Number.isFinite(maxPos)) {
    return Math.max(0, Math.min(mappedPos, maxPos));
  }

  return Math.max(0, mappedPos);
};

const mapRangeInTransaction = (tr, range) => {
  // Map boundaries inward so mapped deletes do not eat adjacent plain text.
  const mappedFrom = mapPosInTransaction(tr, Number(range?.from), 1);
  const mappedTo = mapPosInTransaction(tr, Number(range?.to), -1);
  if (!Number.isFinite(mappedFrom) || !Number.isFinite(mappedTo) || mappedTo <= mappedFrom) {
    return null;
  }
  return { from: mappedFrom, to: mappedTo };
};

const deleteMappedRange = (tr, range) => {
  const mappedRange = mapRangeInTransaction(tr, range);
  if (!mappedRange) return false;
  try {
    tr.delete(mappedRange.from, mappedRange.to);
    return true;
  } catch (err) {
    return false;
  }
};

const deleteRangeWithTableFallback = (tr, range, fallbackPos = null) => {
  const mappedRange = mapRangeInTransaction(tr, range);
  if (!mappedRange) return false;

  try {
    tr.delete(mappedRange.from, mappedRange.to);
    return true;
  } catch (err) {
    // Fall back to deleting the ancestor table if direct mapped delete fails.
  }

  try {
    const mappedFallbackPos = Number.isFinite(Number(fallbackPos))
      ? mapPosInTransaction(tr, Number(fallbackPos), -1)
      : mappedRange.from;
    const tableRange = resolveAncestorTableRange(tr?.doc, Number.isFinite(mappedFallbackPos) ? mappedFallbackPos : mappedRange.from);
    if (!tableRange) return false;
    try {
      tr.delete(tableRange.from, tableRange.to);
      return true;
    } catch (nestedErr) {
      return false;
    }
  } catch (err) {
    return false;
  }
};

const collectStructuralBoundaryMarkers = (state, matchAttrs = null) => {
  const doc = state?.doc;
  const removeType = state?.schema?.marks?.[REMOVE_MARK_NAME];
  if (!doc || typeof doc.nodesBetween !== 'function' || !removeType) return [];

  const matchesAttrs = typeof matchAttrs === 'function'
    ? matchAttrs
    : () => true;

  const markers = [];
  doc.nodesBetween(0, Number(doc?.content?.size || 0), (node, pos) => {
    if (!node?.isText) return;

    const marks = Array.isArray(node.marks) ? node.marks : [];
    const removeMark = marks.find((mark) => mark?.type === removeType);
    if (!removeMark) return;

    const attrs = removeMark?.attrs || {};
    if (!matchesAttrs(attrs)) return;

    const markerType = String(attrs?.marker_type || '').trim().toLowerCase();
    if (markerType !== 'paragraph-break' && markerType !== 'hard-break') return;

    const text = String(node.text || '');
    if (!text) return;

    for (let index = 0; index < text.length; index += 1) {
      const char = text.charAt(index);
      const matchesChar = markerType === 'paragraph-break'
        ? char === PARAGRAPH_BREAK_SUGGESTION_CHAR
        : char === HARD_BREAK_SUGGESTION_CHAR;
      if (!matchesChar) continue;

      markers.push({
        pos: Number(pos || 0) + index,
        markerType,
        markerDirection: String(attrs?.marker_direction || '').trim().toLowerCase()
      });
    }
  });

  return markers.sort((a, b) => Number(b?.pos || 0) - Number(a?.pos || 0));
};

const removeMarkerCharAt = (tr, pos) => {
  const from = Math.max(0, Number(pos || 0));
  const token = String(tr?.doc?.textBetween?.(from, from + 1, '', '') || '');
  if (!isStructuralSuggestionMarkerChar(token)) return false;
  try {
    tr.delete(from, from + 1);
    return true;
  } catch (err) {
    return false;
  }
};

const realizeStructuralBoundaryMarker = (originalState, tr, marker) => {
  if (!tr?.doc || !Number.isFinite(Number(marker?.pos))) return;

  const mappedPos = Math.max(0, tr.mapping.map(Number(marker.pos), -1));
  const markerType = String(marker?.markerType || '').trim().toLowerCase();
  const markerDirection = String(marker?.markerDirection || '').trim().toLowerCase();

  if (markerType === 'hard-break') {
    // Remove the visual marker first so boundary adjacency checks are against
    // the actual document structure (hardBreak before/after boundary).
    removeMarkerCharAt(tr, mappedPos);

    let $pos;
    try {
      $pos = tr.doc.resolve(mappedPos);
    } catch (err) {
      $pos = null;
    }

    const afterType = String($pos?.nodeAfter?.type?.name || '').trim().toLowerCase();
    const beforeType = String($pos?.nodeBefore?.type?.name || '').trim().toLowerCase();

    if (afterType === 'hardbreak') {
      deleteMappedRange(tr, { from: mappedPos, to: mappedPos + 1 });
    } else if (beforeType === 'hardbreak' && mappedPos > 0) {
      deleteMappedRange(tr, { from: mappedPos - 1, to: mappedPos });
    }
    return;
  }

  // paragraph-break: remove marker char, then replay ProseMirror's own
  // joinForward / joinBackward command so the structural result exactly
  // matches what TipTap would have done for the original keystroke.
  //
  // Strategy:
  //   1. Delete the marker char in tr (so tr.doc is the "clean" post-removal doc).
  //   2. Apply tr to originalState to get an intermediate EditorState whose
  //      .doc === tr.doc. Steps produced against that state are safe to append to tr.
  //   3. Place a cursor at the boundary position in that intermediate state.
  //   4. Run joinForward or joinBackward (prosemirror-commands). These commands
  //      internally handle all structural cases: same-type joins, lifting from
  //      lists, merging across different node depths, etc. This keeps "keep"
  //      behaviour in sync with TipTap automatically as the schema evolves.
  //   5. Capture the dispatched steps and append them to tr.
  removeMarkerCharAt(tr, mappedPos);

  let intermediateState;
  try {
    intermediateState = originalState.apply(tr);
  } catch (err) {
    return;
  }

  // Cursor position in the intermediate doc is the mapped boundary position.
  const cursorPos = Math.max(
    0,
    Math.min(tr.mapping.map(mappedPos), Number(intermediateState.doc?.content?.size || 0))
  );

  let stateForJoin;
  try {
    const setSelTr = intermediateState.tr.setSelection(
      TextSelection.create(intermediateState.doc, cursorPos)
    );
    stateForJoin = intermediateState.apply(setSelTr);
  } catch (err) {
    return;
  }

  // Capture the steps (and resulting selection) emitted by a PM join command.
  let capturedSteps = [];
  let capturedSelection = null;
  const captureDispatch = (joinTr) => {
    capturedSteps = Array.isArray(joinTr?.steps) ? [...joinTr.steps] : [];
    capturedSelection = joinTr?.selection ?? null;
  };

  // Detect list context to determine the correct structural transformation.
  //
  //   Case A — equal-depth li→li: cursor's li has exactly one child (just the p)
  //     AND the adjacent sibling in the same ul is also a listItem.
  //     → joinForward×2 (or joinBackward×2): first join merges the two lis,
  //       second merges the two inner ps into one line.
  //
  //   Case B — adjacent list is deeper: cursor's li has a nested ul child.
  //     → single joinForward/joinBackward pass, already correct.
  //
  //   Case C — adjacent list is higher: cursor is first/last li in a nested ul,
  //     parent li has no more same-depth siblings but the OUTER ul has an
  //     adjacent li. TipTap does joinForward().joinBackward(), but PM's joinForward
  //     merely merges the two outer lis (bullet removed, content not indented) and
  //     joinBackward then fails because the cursor is at the END of its textblock.
  //     → Direct transaction: cut the outer adjacent li and insert it into the
  //       inner ul, replicating the TipTap "sink" result without relying on PM
  //       commands that operate incorrectly at this depth.
  //
  //   Default — non-list or plain paragraph boundary:
  //     → single joinForward or joinBackward.

  let caseA = false, caseC = false;
  // Case C params (set inside try block, used after)
  let caseC_insertPos, caseC_insertNode, caseC_deleteFrom, caseC_deleteTo;

  try {
    const $b = stateForJoin.doc.resolve(cursorPos);
    if ($b.parent.isTextblock && $b.depth >= 3) {
      const liNode = $b.node($b.depth - 1);
      if (liNode?.type?.name === 'listItem') {
        const ulDepth = $b.depth - 2;
        const ulNode = $b.node(ulDepth);
        const liIndex = $b.index(ulDepth);
        const adjacentLi = markerDirection === 'backspace'
          ? ulNode.maybeChild(liIndex - 1)
          : ulNode.maybeChild(liIndex + 1);

        if (liNode.childCount === 1 && adjacentLi?.type?.name === 'listItem') {
          // Case A: equal-depth sibling — handled below with two join passes.
          caseA = true;
        } else if (!adjacentLi && $b.depth >= 5) {
          const parentLiDepth = ulDepth - 1;
          const outerUlDepth = ulDepth - 2;
          const outerUlNode = $b.node(outerUlDepth);
          const parentLiIndex = $b.index(outerUlDepth);
          const outerAdjacentLi = markerDirection === 'backspace'
            ? outerUlNode.maybeChild(parentLiIndex - 1)
            : outerUlNode.maybeChild(parentLiIndex + 1);
          if ($b.node(parentLiDepth)?.type?.name === 'listItem'
              && outerAdjacentLi?.type?.name === 'listItem') {
            caseC = true;
            if (markerDirection === 'backspace') {
              // Move outer prev-sibling li to the START of the inner ul.
              // prevLi is before li[A] → before innerUlStart → delete pos unaffected by insert.
              const innerUlStart = $b.start(ulDepth);
              const prevLiStart = $b.before(parentLiDepth) - outerAdjacentLi.nodeSize;
              caseC_insertPos = innerUlStart;
              caseC_insertNode = outerAdjacentLi;
              caseC_deleteFrom = prevLiStart;           // unshifted (before insert pos)
              caseC_deleteTo = prevLiStart + outerAdjacentLi.nodeSize;
            } else {
              // Move outer next-sibling li to the END of the inner ul.
              // nextLi is after li[A] → after innerUlEnd → shifts by nodeSize after insert.
              const innerUlEnd = $b.end(ulDepth);
              const nextLiStart = $b.after(parentLiDepth);
              caseC_insertPos = innerUlEnd;
              caseC_insertNode = outerAdjacentLi;
              caseC_deleteFrom = nextLiStart + outerAdjacentLi.nodeSize;   // shifted
              caseC_deleteTo = nextLiStart + 2 * outerAdjacentLi.nodeSize; // shifted
            }
          }
        }
      }
    }
  } catch (e) { /* non-list context */ }

  if (caseC) {
    // Direct structural move: insert adjacent outer li into the inner ul, then
    // delete the original. No PM join commands needed or correct here.
    try {
      tr.insert(caseC_insertPos, caseC_insertNode);
      deleteMappedRange(tr, { from: caseC_deleteFrom, to: caseC_deleteTo });
    } catch (e) {}
    return;
  }

  // Cases A, B (deeper nested — single pass is correct), and default paragraph boundary.
  let commands = [markerDirection === 'backspace' ? joinBackward : joinForward];
  if (caseA) {
    commands = markerDirection === 'backspace'
      ? [joinBackward, joinBackward]
      : [joinForward, joinForward];
  }
  // caseB: single pass, already set above.

  let currentJoinState = stateForJoin;
  for (let i = 0; i < commands.length; i++) {
    capturedSteps = [];
    capturedSelection = null;
    try {
      commands[i](currentJoinState, captureDispatch);
    } catch (err) {
      break;
    }
    if (capturedSteps.length === 0) break;

    for (const step of capturedSteps) {
      try {
        tr.step(step);
      } catch (err) {
        // Step became invalid after a prior step shifted positions — skip.
      }
    }

    if (i < commands.length - 1) {
      try {
        currentJoinState = originalState.apply(tr);
        if (capturedSelection) {
          try {
            const reselTr = currentJoinState.tr.setSelection(
              TextSelection.create(currentJoinState.doc, capturedSelection.from)
            );
            currentJoinState = currentJoinState.apply(reselTr);
          } catch (e) { /* keep currentJoinState selection as-is */ }
        }
      } catch (err) {
        break;
      }
    }
  }
};

const collectStructuralMarkerCharRanges = (state, ranges = [], markType = null) => {
  const doc = state?.doc;
  if (!doc || typeof doc.nodesBetween !== 'function' || !markType || !Array.isArray(ranges) || !ranges.length) return [];

  const out = [];
  const pushRange = (from, to) => {
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return;
    out.push({ from, to });
  };

  for (const range of ranges) {
    const from = Number(range?.from);
    const to = Number(range?.to);
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) continue;

    doc.nodesBetween(from, to, (node, pos) => {
      if (!node?.isText) return;

      const marks = Array.isArray(node.marks) ? node.marks : [];
      const hasRemove = marks.some((mark) => mark?.type === markType);
      if (!hasRemove) return;

      const text = String(node.text || '');
      if (!text) return;

      const start = Number(pos || 0);
      for (let index = 0; index < text.length; index += 1) {
        const char = text.charAt(index);
        if (!isStructuralSuggestionMarkerChar(char)) continue;

        const charFrom = start + index;
        const charTo = charFrom + 1;
        if (charFrom < from || charTo > to) continue;
        pushRange(charFrom, charTo);
      }
    });
  }

  return sortRangesDescending(out);
};

export const resolveSuggestionByAnnotationId = (editor, { annotationId, action } = {}) => {
  try {
  const normalizedAction = String(action || '').trim().toLowerCase();
  const normalizedId = normalizeAnnotationId(annotationId);
  const state = editor?.state;
  const view = editor?.view;

  if (!state || !view?.dispatch || !normalizedId) {
    return { changed: false, reason: 'invalid-context' };
  }

  if (normalizedAction !== 'keep' && normalizedAction !== 'undo') {
    return { changed: false, reason: 'invalid-action' };
  }

  const { addRanges, removeRanges } = collectMarkRangesByAnnotationId(state, normalizedId);
  const addType = state.schema?.marks?.[ADD_MARK_NAME] || null;
  const removeType = state.schema?.marks?.[REMOVE_MARK_NAME] || null;

  const structuralNodes = collectStructuralNodeAnnotations(state, (node) => {
    return normalizeAnnotationId(node.attrs?.diffAnnotationId) === normalizedId;
  });

  if (addRanges.length === 0 && removeRanges.length === 0 && structuralNodes.length === 0) {
    return { changed: false, reason: 'annotation-not-found' };
  }

  const tr = state.tr;
  const structuralBoundaryMarkers = collectStructuralBoundaryMarkers(
    state,
    (attrs) => normalizeAnnotationId(attrs?.annotation_id) === normalizedId
  );
  const structuralBoundaryPositions = structuralBoundaryMarkers
    .map((marker) => Number(marker?.pos))
    .filter((pos) => Number.isFinite(pos) && pos >= 0);
  const removeRangesWithBoundaryMarkers = removeRanges.filter((range) => {
    const from = Number(range?.from);
    const to = Number(range?.to);
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return false;
    return structuralBoundaryPositions.some((pos) => pos >= from && pos < to);
  });
  const removeRangesWithoutBoundaryMarkers = removeRanges.filter((range) => {
    const from = Number(range?.from);
    const to = Number(range?.to);
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return false;
    return !structuralBoundaryPositions.some((pos) => pos >= from && pos < to);
  });
  const structuralRemoveNodes = structuralNodes.filter(isStructuralRemoveNode);
  const structuralAddNodes = structuralNodes.filter(isStructuralAddNode);

  if (normalizedAction === 'keep') {
    if (addType) {
      for (const range of addRanges) {
        tr.removeMark(range.from, range.to, addType);
      }
    }

    if (removeType) {
      for (const range of removeRangesWithBoundaryMarkers) {
        tr.removeMark(range.from, range.to, removeType);
      }
    }

    for (const marker of structuralBoundaryMarkers) {
      realizeStructuralBoundaryMarker(state, tr, marker);
    }

    // Accept structural adds by keeping the node and clearing suggestion attrs.
    for (const sn of [...structuralAddNodes].sort((a, b) => Number(b?.pos || 0) - Number(a?.pos || 0))) {
      try {
        tr.setNodeMarkup(sn.pos, undefined, clearStructuralSuggestionAttrs(sn.attrs));
      } catch (err) {}
    }

    // Accept structural removes by deleting those nodes.
    for (const range of mergeKeepDeleteRanges(state, removeRangesWithoutBoundaryMarkers, structuralRemoveNodes)) {
      const structuralSource = structuralRemoveNodes.find((node) => Number(node?.pos) === Number(range?.from));
      const isTableStructural = ['table', 'tableRow', 'tableCell', 'tableHeader'].includes(String(structuralSource?.name || ''));
      if (isTableStructural) {
        deleteRangeWithTableFallback(tr, range, Number(structuralSource?.pos));
      } else {
        tr.delete(range.from, range.to);
      }
    }
  } else {
    const structuralMarkerDeleteRanges = collectStructuralMarkerCharRanges(state, removeRanges, removeType);

    if (removeType) {
      for (const range of removeRanges) {
        tr.removeMark(range.from, range.to, removeType);
      }
    }

    for (const range of structuralMarkerDeleteRanges) {
      deleteMappedRange(tr, range);
    }

    // Undo structural removes by keeping the node and clearing suggestion attrs.
    for (const sn of [...structuralRemoveNodes].sort((a, b) => Number(b?.pos || 0) - Number(a?.pos || 0))) {
      try {
        tr.setNodeMarkup(sn.pos, undefined, clearStructuralSuggestionAttrs(sn.attrs));
      } catch (err) {}
    }

    // Undo structural adds by deleting those nodes, plus inline add ranges.
    for (const range of mergeUndoDeleteRanges(getUndoDeleteRanges(state, addRanges), structuralAddNodes)) {
      const structuralSource = structuralAddNodes.find((node) => Number(node?.pos) === Number(range?.from));
      const isTableStructural = ['table', 'tableRow', 'tableCell', 'tableHeader'].includes(String(structuralSource?.name || ''));
      if (isTableStructural) {
        deleteRangeWithTableFallback(tr, range, Number(structuralSource?.pos));
      } else {
        deleteMappedRange(tr, range);
      }
    }
  }

  if (!tr.docChanged) {
    return { changed: false, reason: 'no-op' };
  }

  view.dispatch(tr);
  return {
    changed: true,
    annotationId: normalizedId,
    action: normalizedAction,
    addRangeCount: addRanges.length,
    removeRangeCount: removeRanges.length
  };
  } catch (err) {
    return { changed: false, reason: 'resolve-error' };
  }
};

export const resolveSuggestionByBatchId = (editor, { batchId, action } = {}) => {
  try {
  const normalizedAction = String(action || '').trim().toLowerCase();
  const normalizedBatchId = normalizeBatchId(batchId);
  const state = editor?.state;
  const view = editor?.view;

  if (!state || !view?.dispatch || !normalizedBatchId) {
    return { changed: false, reason: 'invalid-context' };
  }

  if (normalizedAction !== 'keep' && normalizedAction !== 'undo') {
    return { changed: false, reason: 'invalid-action' };
  }

  const { addRanges, removeRanges } = collectMarkRangesByBatchId(state, normalizedBatchId);
  const addType = state.schema?.marks?.[ADD_MARK_NAME] || null;
  const removeType = state.schema?.marks?.[REMOVE_MARK_NAME] || null;

  const structuralNodes = collectStructuralNodeAnnotations(state, (node) => {
    return normalizeBatchId(node.attrs?.diffBatchId) === normalizedBatchId;
  });

  if (addRanges.length === 0 && removeRanges.length === 0 && structuralNodes.length === 0) {
    return { changed: false, reason: 'batch-not-found' };
  }

  const tr = state.tr;
  const structuralBoundaryMarkers = collectStructuralBoundaryMarkers(
    state,
    (attrs) => normalizeBatchId(attrs?.batch_id) === normalizedBatchId
  );
  const structuralBoundaryPositions = structuralBoundaryMarkers
    .map((marker) => Number(marker?.pos))
    .filter((pos) => Number.isFinite(pos) && pos >= 0);
  const removeRangesWithBoundaryMarkers = removeRanges.filter((range) => {
    const from = Number(range?.from);
    const to = Number(range?.to);
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return false;
    return structuralBoundaryPositions.some((pos) => pos >= from && pos < to);
  });
  const removeRangesWithoutBoundaryMarkers = removeRanges.filter((range) => {
    const from = Number(range?.from);
    const to = Number(range?.to);
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return false;
    return !structuralBoundaryPositions.some((pos) => pos >= from && pos < to);
  });
  const structuralRemoveNodes = structuralNodes.filter(isStructuralRemoveNode);
  const structuralAddNodes = structuralNodes.filter(isStructuralAddNode);

  if (normalizedAction === 'keep') {
    if (addType) {
      for (const range of addRanges) {
        tr.removeMark(range.from, range.to, addType);
      }
    }

    if (removeType) {
      for (const range of removeRangesWithBoundaryMarkers) {
        tr.removeMark(range.from, range.to, removeType);
      }
    }

    for (const marker of structuralBoundaryMarkers) {
      realizeStructuralBoundaryMarker(state, tr, marker);
    }

    for (const sn of [...structuralAddNodes].sort((a, b) => Number(b?.pos || 0) - Number(a?.pos || 0))) {
      try {
        tr.setNodeMarkup(sn.pos, undefined, clearStructuralSuggestionAttrs(sn.attrs));
      } catch (err) {}
    }

    for (const range of mergeKeepDeleteRanges(state, removeRangesWithoutBoundaryMarkers, structuralRemoveNodes)) {
      const structuralSource = structuralRemoveNodes.find((node) => Number(node?.pos) === Number(range?.from));
      const isTableStructural = ['table', 'tableRow', 'tableCell', 'tableHeader'].includes(String(structuralSource?.name || ''));
      if (isTableStructural) {
        deleteRangeWithTableFallback(tr, range, Number(structuralSource?.pos));
      } else {
        tr.delete(range.from, range.to);
      }
    }
  } else {
    const structuralMarkerDeleteRanges = collectStructuralMarkerCharRanges(state, removeRanges, removeType);

    if (removeType) {
      for (const range of removeRanges) {
        tr.removeMark(range.from, range.to, removeType);
      }
    }

    for (const range of structuralMarkerDeleteRanges) {
      deleteMappedRange(tr, range);
    }

    for (const sn of [...structuralRemoveNodes].sort((a, b) => Number(b?.pos || 0) - Number(a?.pos || 0))) {
      try {
        tr.setNodeMarkup(sn.pos, undefined, clearStructuralSuggestionAttrs(sn.attrs));
      } catch (err) {}
    }

    for (const range of mergeUndoDeleteRanges(getUndoDeleteRanges(state, addRanges), structuralAddNodes)) {
      const structuralSource = structuralAddNodes.find((node) => Number(node?.pos) === Number(range?.from));
      const isTableStructural = ['table', 'tableRow', 'tableCell', 'tableHeader'].includes(String(structuralSource?.name || ''));
      if (isTableStructural) {
        deleteRangeWithTableFallback(tr, range, Number(structuralSource?.pos));
      } else {
        deleteMappedRange(tr, range);
      }
    }
  }

  if (!tr.docChanged) {
    return { changed: false, reason: 'no-op' };
  }

  view.dispatch(tr);
  return {
    changed: true,
    batchId: normalizedBatchId,
    action: normalizedAction,
    addRangeCount: addRanges.length,
    removeRangeCount: removeRanges.length
  };
  } catch (err) {
    return { changed: false, reason: 'resolve-error' };
  }
};

// ---------------------------------------------------------------------------
// Table structural suggestion helpers
// ---------------------------------------------------------------------------

/**
 * Returns structural context if [from, to] covers a complete table or complete
 * table row(s). Returns null otherwise.
 *
 * Return shapes:
 *   { type: 'table', from, to }
 *   { type: 'rows',  from, to, rowRanges: [{from, to}] }
 */
export const getTableSuggestionContext = (state, from, to) => {
  const doc = state?.doc;
  if (!doc || !Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;

  // 1. Check if a complete table is fully enclosed within [from, to].
  let fullTableInfo = null;
  try {
    doc.nodesBetween(from, to, (node, pos) => {
      if (node.type.name !== 'table') return true;
      const tableEnd = pos + node.nodeSize;
      if (pos >= from && tableEnd <= to) {
        fullTableInfo = { from: pos, to: tableEnd };
      }
      return false; // don't descend into table
    });
  } catch (err) {
    return null;
  }

  if (fullTableInfo) return { type: 'table', from: fullTableInfo.from, to: fullTableInfo.to };

  // 2. Check if one or more complete tableRow nodes are within [from, to].
  const touchedRows = [];
  try {
    doc.nodesBetween(from, to, (node, pos) => {
      const name = node.type.name;
      if (name === 'table') return true; // descend into table
      if (name === 'tableRow') {
        touchedRows.push({ pos, size: node.nodeSize });
        return false; // don't descend into row
      }
      return true;
    });
  } catch (err) {
    return null;
  }

  if (touchedRows.length === 0) return null;

  // All touched rows must be fully covered. Use content-boundary check
  // (pos+1 to pos+size-1) to accommodate CellSelection whose from/to sit at
  // cell content boundaries rather than row opening/closing tokens.
  const coveredRows = touchedRows.filter(r => {
    const rowContentStart = r.pos + 1;
    const rowContentEnd = r.pos + r.size - 1;
    return from <= rowContentStart && to >= rowContentEnd;
  });

  if (coveredRows.length === 0 || coveredRows.length !== touchedRows.length) return null;

  // Rows must be contiguous (all from the same table, no gaps).
  for (let i = 1; i < coveredRows.length; i++) {
    if (coveredRows[i].pos !== coveredRows[i - 1].pos + coveredRows[i - 1].size) return null;
  }

  return {
    type: 'rows',
    from: coveredRows[0].pos,
    to: coveredRows[coveredRows.length - 1].pos + coveredRows[coveredRows.length - 1].size,
    rowRanges: coveredRows.map(r => ({ from: r.pos, to: r.pos + r.size }))
  };
};

/**
 * Strips all diffSuggestionAdd and diffSuggestionRemove marks from [from, to]
 * in an existing transaction. Mutates tr and returns it.
 */
export const clearSuggestionMarksInRange = (tr, state, from, to) => {
  const addType = state?.schema?.marks?.diffSuggestionAdd;
  const removeType = state?.schema?.marks?.diffSuggestionRemove;
  if (addType) tr.removeMark(from, to, addType);
  if (removeType) tr.removeMark(from, to, removeType);
  return tr;
};

// Finds structural table-family nodes (table / tableRow / tableCell / tableHeader)
// with a diffOp annotation that
// satisfies matchFn, returning [{pos, nodeSize, attrs, name}] sorted by pos.
const collectStructuralNodeAnnotations = (state, matchFn) => {
  const results = [];
  const doc = state?.doc;
  if (!doc || typeof matchFn !== 'function') return results;
  try {
    doc.nodesBetween(0, doc.content.size, (node, pos) => {
      const name = node.type.name;
      if (name !== 'table' && name !== 'tableRow' && name !== 'tableCell' && name !== 'tableHeader') return true;
      const diffOp = String(node.attrs?.diffOp || '').trim();
      if (!diffOp) return false; // no structural annotation, skip subtree
      if (matchFn(node)) {
        results.push({ pos, nodeSize: node.nodeSize, attrs: node.attrs, name });
      }
      return false; // don't descend into annotated node
    });
  } catch (err) {}
  return results;
};

const suggestionHelpers = {
  createSuggestionIdentity,
  createSuggestionMarkAttrs,
  buildSuggestionTextNode,
  buildAiWriteSuggestionPair,
  resolveSuggestionByAnnotationId,
  resolveSuggestionByBatchId
};

export default suggestionHelpers;